import React, { useRef, useEffect, useCallback, useState, useContext, useMemo } from 'react';
import { Button, Checkbox, Icon, Modal, Select, TextArea } from '@aidkitorg/component-library';
import type { UIMessage, ToolUIPart, DynamicToolUIPart, InferUITools } from 'ai';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, isToolUIPart, jsonSchema } from 'ai';
import { useChat } from '@ai-sdk/react';
import { useMarkdown, safeParse } from '../Util';
import { streamingApiPath, useToken } from '../API';
import { useCopyToClipboard } from '../Hooks/CopyToClipboard';
import type { Root } from '@aidkitorg/types/lib/survey';
import type { distroKindsTools } from '@aidkitorg/aidkit/lib/distro/ai/distroKindsTool';
import type { configTools, editConfigTools } from '@aidkitorg/aidkit/lib/distro/ai/configTools';
import type { makeDbSchemaTools } from '@aidkitorg/aidkit/lib/distro/ai/dbSchemaTools';
import {
  getConfigObject as getObject,
  searchConfig,
  summarizeConfig as getConfigSummary,
} from '@aidkitorg/typesheets/lib/configAgent';
import { boundToolResult, resolveRawPath, runCopyConfig, runEditConfig, type TransactionApplier } from './editConfigTool';
import type { RemoteSurveyResult } from '../collabRemote';
import { SurveyName } from '@aidkitorg/types/lib/distroFileNames';
import { formatIssues } from './validationDiff';
import type { ValidationResult } from '@aidkitorg/types/lib/distroValidator/distroValidator';
import { MODEL_USD_PER_MIL_TOKEN_RATES } from '@aidkitorg/types/lib/modelRates';
import { cleanupInterruptedToolParts, messagesAfterLeavingPiiMode, prepareOutgoingMessages } from './aiChatTranscript';
import { AIDKIT_STREAMING_AUTH_HEADER, fetchWithAwsPayloadHash } from './streamingRequest';
import type { AskAIContext } from '@aidkitorg/typesheets/lib/askAI';
import { buildAskAIMessage } from './askAIContext';

type ChatTools = InferUITools<typeof distroKindsTools & typeof configTools & typeof editConfigTools & ReturnType<typeof makeDbSchemaTools>>;

/**
 * How recoverable an AI edit is here, which is all the warning copy really cares about.
 * - 'branch': collab on, on a non-default branch — delete the branch to discard.
 * - 'default-branch': collab on, but on the dev branch — nothing to delete.
 * - 'no-branches': collab off, so there are no branches at all — discard by
 *   reloading without saving. Editing still works; the editor's document is a
 *   CRDT either way, collab only decides whether changes are broadcast.
 * - 'unknown': the current branch id doesn't match any branch we know about, so
 *   we can't promise there's anything to delete. Treated as unsafe.
 */
export type EditSafety = 'branch' | 'default-branch' | 'no-branches' | 'unknown';

/**
 * All the per-case edit-mode warning copy in one place — the banner and the
 * confirmation modal say overlapping things, and they must not drift apart.
 */
const EDIT_SAFETY_COPY: Record<EditSafety, {
  banner: string;
  message: (branchName?: string) => React.ReactNode;
  /** Extra call-out in the modal, when edits here can't be thrown away wholesale. */
  modalNote?: (branchName?: string) => React.ReactNode;
  confirmLabel: string;
}> = {
  'branch': {
    banner: 'bg-amber-50 border-amber-200 text-amber-800',
    message: (branchName) => <>
      The assistant can change this config. Edits land as unpublished changes on <strong>{branchName}</strong> —
      discard them by deleting the branch.
    </>,
    confirmLabel: 'Enable edits',
  },
  'default-branch': {
    banner: 'bg-red-50 border-red-200 text-red-800',
    message: (branchName) => <>
      <strong>Edits apply directly to the {branchName} branch.</strong>{' '}
      Switch to a non-default branch first so you can throw the changes away by deleting it.
    </>,
    modalNote: (branchName) => <>
      You're on <strong>{branchName}</strong>, the default branch. Create or switch to a branch
      before turning this on.
    </>,
    confirmLabel: 'Enable anyway',
  },
  'unknown': {
    banner: 'bg-red-50 border-red-200 text-red-800',
    message: () => <>
      <strong>Can't tell which branch this is.</strong>{' '}
      There may be nothing to delete to undo these edits — reload the page to get back to a known branch.
    </>,
    modalNote: () => <>
      The current branch doesn't match any branch we know about — usually a stale link, or a branch
      deleted in another tab. Reload the page and check which branch you're on before editing here.
    </>,
    confirmLabel: 'Enable anyway',
  },
  'no-branches': {
    banner: 'bg-red-50 border-red-200 text-red-800',
    message: () => <>
      <strong>Collab is off, so there are no branches to discard.</strong>{' '}
      Your only way back is to reload the page without saving.
    </>,
    modalNote: () => <>
      Collab is off for this config, so there are no branches — there is nothing to delete to
      undo the assistant's work. Your only way back is to reload the page without saving.
      Turn on collab first if you want a branch to throw away.
    </>,
    confirmLabel: 'Enable anyway',
  },
};

/**
 * Extract text content from a UIMessage's parts array.
 */
function getMessageText(message: UIMessage): string {
  return message.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map(p => p.text)
    .join('');
}

// Pasting a bare component object onto an array-typed Distro prop fails; a
// 1-element array pastes everywhere (prop paste unwraps it). Only objects that
// look like Distro components (have a `kind`) get wrapped — non-config JSON is
// left verbatim. Applied to the rendered block AND the copied text so users
// see exactly what the clipboard will contain.
function isBareDistroComponent(parsed: unknown): boolean {
  return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    && typeof (parsed as { kind?: unknown }).kind === 'string';
}

function wrapBareComponentJson(text: string): string {
  return isBareDistroComponent(safeParse(text, null)) ? `[\n${text}\n]` : text;
}

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const { copied, copyToClipboard } = useCopyToClipboard();
  return (
    <button
      onClick={() => copyToClipboard(text)}
      className="absolute top-1 right-1 px-1.5 py-0.5 text-[10px] rounded bg-gray-200 hover:bg-gray-300 text-gray-600"
      title="Copy to clipboard"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

/**
 * Split assistant message content into text segments and JSON code blocks.
 * JSON code blocks get a copy button.
 */
const AssistantUIMessage: React.FC<{ content: string }> = ({ content }) => {
  // Match all fenced code blocks. Capture the body (group 1).
  const codeBlockRegex = /```\w*\s*\n([\s\S]*?)```/g;
  const parts: { type: 'text' | 'code'; content: string }[] = [];
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'code', content: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return (
    <div className="w-full ai-chat-markdown">
      {parts.map((part, i) => {
        if (part.type === 'code') {
          const code = wrapBareComponentJson(part.content);
          return (
            <div key={i} className="relative my-2">
              <CopyButton text={code} />
              <pre className="text-xs bg-gray-800 text-gray-100 rounded p-3 pr-16 overflow-x-auto">
                <code>{code}</code>
              </pre>
            </div>
          );
        }
        return (
          <div key={i} className="text-sm text-gray-700 break-words whitespace-pre-wrap">
            <TextContent content={part.content} />
          </div>
        );
      })}
    </div>
  );
};

const TextContent: React.FC<{ content: string }> = ({ content }) => {
  const markdownContent = useMarkdown(content);
  return <>{markdownContent}</>;
};

type AnyToolUIPart = ToolUIPart<ChatTools> | DynamicToolUIPart;

/** How an editConfig row reads at a glance — an edit is the one tool call that changes state. */
const EDIT_ROW_STYLE = {
  diverged: { box: 'border-red-300 bg-red-100', name: 'text-red-800', status: 'text-red-700', label: 'DIVERGED — reload' },
  failed: { box: 'border-red-200 bg-red-50', name: 'text-red-700', status: 'text-red-600', label: 'rejected' },
  warn: { box: 'border-blue-200 bg-blue-50', name: 'text-blue-700', status: 'text-amber-700', label: 'applied, warnings' },
  dryRun: { box: 'border-blue-200 bg-blue-50', name: 'text-blue-700', status: 'text-blue-700', label: 'dry run ok' },
  ok: { box: 'border-blue-200 bg-blue-50', name: 'text-blue-700', status: 'text-blue-700', label: 'applied' },
} as const;


function getToolDisplayName(part: AnyToolUIPart): string {
  if ('toolName' in part) return part.toolName;
  if (part.type.startsWith('tool-')) return part.type.slice(5);
  return 'unknown tool';
}

const ToolInvocationPart: React.FC<{ part: AnyToolUIPart }> = ({ part }) => {
  const [expanded, setExpanded] = useState(false);
  const toolName = getToolDisplayName(part);
  const hasOutput = part.state === 'output-available' && part.output !== undefined;
  const hasError = part.state === 'output-error';
  const isRunning = part.state === 'input-available' || part.state === 'input-streaming';
  const outputStr = hasOutput ? JSON.stringify(part.output, null, 2) : '';
  const isEdit = part.type === 'tool-editConfig';
  // copyConfigObject writes through the same engine, so its rows read the same way.
  const isWrite = isEdit || part.type === 'tool-copyConfigObject';
  // editConfig carries whole components in its input; showing that raw blows the
  // row apart. The op count identifies the call at a glance.
  const editInput = part.input as { edits?: string, dryRun?: boolean } | undefined;
  const editOpsSummary = (() => {
    if (!isEdit) return '';
    const parsed = safeParse(editInput?.edits ?? '', null);
    const count = Array.isArray(parsed) ? parsed.length : null;
    return `${count === null ? '?' : count} edit${count === 1 ? '' : 's'}${editInput?.dryRun ? ' (dry run)' : ''}`;
  })();
  const inputStr = part.input && Object.keys(part.input).length > 0
    ? isEdit ? editOpsSummary : JSON.stringify(part.input)
    : '';

  // One read of the edit result: the tool returns a result object rather than
  // throwing, so a rejected transaction is still an 'output-available' part.
  const editOutput = isWrite && hasOutput
    ? part.output as { ok?: boolean, diverged?: boolean, dryRun?: boolean, warnings?: string[], errors?: string[] }
    : undefined;
  const editNotes = editOutput?.ok ? (editOutput.warnings ?? []) : (editOutput?.errors ?? []);
  const editStatus = !editOutput ? undefined
    : editOutput.diverged ? 'diverged'
      : editOutput.ok !== true ? 'failed'
        : (editOutput.warnings?.length ? 'warn' : editOutput.dryRun ? 'dryRun' : 'ok');

  // Pretty-printed JSON from a successful validateConfig call, for inline display + copy.
  // Bare components are array-wrapped here so display and clipboard match.
  const prettyValidatedJson: string | null = (() => {
    if (part.type !== 'tool-validateConfig' || part.state !== 'output-available') return null;
    if (!part.output.valid) return null;
    const parsed = safeParse(part.input.json, null);
    if (parsed === null) return null;
    return JSON.stringify(isBareDistroComponent(parsed) ? [parsed] : parsed, null, 2);
  })();

  const style = editStatus ? EDIT_ROW_STYLE[editStatus] : undefined;

  return (
    <div className={`text-xs border rounded my-1 ${style?.box ?? 'border-gray-200 bg-gray-50'}`}>
      <button
        onClick={() => hasOutput && setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-left hover:bg-gray-100 rounded"
        disabled={!hasOutput}
      >
        <span className="text-gray-400">{hasOutput ? (expanded ? '▼' : '▶') : isRunning ? '...' : hasError ? '!' : '▶'}</span>
        <span className={`font-medium ${style?.name ?? 'text-gray-600'}`}>{toolName}</span>
        {inputStr && <span className="text-gray-500 font-mono">{inputStr}</span>}
        {style && <span className={`ml-auto ${style.status}`}>{style.label}</span>}
        {!style && hasOutput && !prettyValidatedJson && <span className="text-gray-400 ml-auto">{outputStr.length.toLocaleString()} chars</span>}
        {hasError && <span className="text-red-500 ml-auto">{part.errorText}</span>}
        {isRunning && <span className="text-gray-400 ml-auto">running...</span>}
      </button>
      {editNotes.length > 0 && (
        <ul className={`mx-2 mb-1 list-disc pl-4 text-[11px] ${editOutput?.ok ? 'text-amber-800' : 'text-red-700'}`}>
          {editNotes.slice(0, 8).map((note, i) => <li key={i}>{note}</li>)}
        </ul>
      )}
      {prettyValidatedJson && (
        <div className="relative mx-2 my-1">
          <CopyButton text={prettyValidatedJson} />
          <pre className="text-[11px] bg-gray-800 text-gray-100 rounded p-3 pr-16 overflow-auto max-h-96 whitespace-pre">
            <code>{prettyValidatedJson}</code>
          </pre>
        </div>
      )}
      {expanded && hasOutput && !prettyValidatedJson && (
        <pre className="px-2 py-1.5 border-t border-gray-200 overflow-x-auto max-h-48 text-[11px] text-gray-600 bg-white">
          {outputStr}
        </pre>
      )}
    </div>
  );
};

export type AIChatOpenRequest = {
  draft: string;
  resolveContext: () => AskAIContext | null;
};

interface FloatingAIChatProps {
  onClose: () => void;
  onHeightChange?: (height: number) => void;
  survey?: Root | null;
  /**
   * The config as it is *right now*. The read tools use this rather than the
   * `survey` prop: several tool calls can run in one assistant response, before
   * React re-renders, so an edit followed by a read would otherwise show the model
   * a document without its own edit in it.
   */
  getSurvey?: () => Root | null;
  /** Applies one AI-authored edit to the live document. Omit to disable edit mode entirely. */
  onApplyTransaction?: TransactionApplier;
  /**
   * Current Distro validation failures, for the checkConfigValidity tool.
   * Null = no survey loaded. Pass a doc to validate a cross-survey snapshot
   * instead of the open document.
   */
  getValidationIssues?: (doc?: Root) => ValidationResult[] | null;
  /** Name of the open survey document (entireprogram/adminpanel/...), for cross-survey routing. */
  surveyName?: string;
  /**
   * Fetches a snapshot of a sibling survey document for the read tools'
   * survey param. Omit to disable cross-survey reads.
   */
  loadOtherSurvey?: (name: string, branch?: string) => Promise<RemoteSurveyResult>;
  /**
   * Branch list for the sibling-branch selector. Which branch of the sibling
   * document the AI reads is the USER's choice — the model has no branch param.
   * Null = sibling has no branches (reads as published).
   */
  getSiblingBranchInfo?: () => Promise<{ survey: string, branches: { id: number, name: string, is_dev_branch: boolean }[] } | null>;
  /** Current Distro branch, shown in the edit-mode warning. */
  branchName?: string;
  /** How recoverable edits are here; drives the edit-mode warning. */
  editSafety?: EditSafety;
  /**
   * Stable identity of the document being edited — config name plus branch id.
   * Edit authorization is scoped to this, not to the displayed branch name: two
   * configs, or two branches, can share a label and safety state, and "Save as"
   * changes the config under us without any visible change.
   */
  documentKey?: string;
  /** Re-open the panel on a particular editor field and put its context in the composer. */
  openRequest?: AIChatOpenRequest | null;
}

type AIModel = 'claude-haiku-4-5' | 'claude-sonnet-5-5' | 'claude-opus-5-5' | 'gpt-6-luna' | 'gpt-6-sol';

const MODEL_OPTIONS: { value: AIModel; label: string; hint: string }[] = [
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', hint: 'fast and cheap Anthropic' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', hint: 'balanced Anthropic' },
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5', hint: 'most powerful Anthropic' },
  { value: 'gpt-6-luna', label: 'GPT-6 Luna', hint: 'fast and cheap OpenAI' },
  { value: 'gpt-6-sol', label: 'GPT-6 Sol', hint: 'balanced OpenAI' },
];

/**
 * Where PII mode lands you if you were on an Anthropic model. Balanced rather than cheapest:
 * the mode exists for debugging a real applicant's state, which is not the place to economise
 * on reasoning — and the user can still switch among the OpenAI models.
 */
const PII_MODE_DEFAULT_MODEL: AIModel = 'gpt-6-sol';

const formatRate = (usdPerMilTokens: number) =>
  `$${Number.isInteger(usdPerMilTokens) ? usdPerMilTokens : usdPerMilTokens.toFixed(2)}`;

const modelCostSummary = (model: AIModel): string | null => {
  const rates = MODEL_USD_PER_MIL_TOKEN_RATES[model];
  if (!rates) return null;
  return `${formatRate(rates.freshInput)} in / ${formatRate(rates.output)} out per Mtok`;
};

// Rich labels show the hint/cost inside the dropdown only; the closed trigger
// renders just the model name via customButtonTextRenderer.
const modelSelectOption = (o: typeof MODEL_OPTIONS[number], disabledReason: string | null) => ({
  value: o.value,
  disabled: disabledReason !== null,
  label: (
    <span className="flex flex-col">
      <span>{o.label}</span>
      <span className="text-xs text-gray-500">{o.hint}</span>
      {modelCostSummary(o.value) && (
        <span className="text-xs text-gray-500">{modelCostSummary(o.value)}</span>
      )}
      {disabledReason && (
        <span className="text-xs text-gray-500">{disabledReason} — clear chat to switch</span>
      )}
    </span>
  ),
});

// Keep in sync with LEGACY_MODEL_ALIASES in packages/aidkit/lib/distro/ai/distroAIChat.ts
// so a saved dropped model migrates to its replacement, not the default.
const LEGACY_MODEL_ALIASES: Record<string, AIModel> = {
  'claude-sonnet-4-6': 'claude-sonnet-5-5',
  'claude-sonnet-5': 'claude-sonnet-5-5',
  'claude-opus-4-7': 'claude-opus-5-5',
  'claude-opus-4-8': 'claude-opus-5-5',
  'gpt-5.4-mini': 'gpt-6-luna',
  'gpt-5.6-luna': 'gpt-6-luna',
  'gpt-5.4': 'gpt-6-sol',
  'gpt-5.5': 'gpt-6-sol',
  'gpt-5.6-terra': 'gpt-6-sol',
  'gpt-5.6-sol': 'gpt-6-sol',
};

const MODEL_CONTEXT_WINDOWS: Record<AIModel, number> = {
  'claude-haiku-4-5': 200_000,
  'claude-sonnet-5-5': 1_000_000,
  'claude-opus-5-5': 1_000_000,
  'gpt-6-luna': 1_000_000,
  'gpt-6-sol': 1_000_000,
};

type UsageStats = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  estimatedCost?: number;
  /** Model that produced the turn; gates replaying an Anthropic compaction from it. */
  model?: string;
};

type ChatDataParts = {
  'usage': UsageStats;
  // model: which model produced the block — it's encrypted per-model and only
  // replayed against a match. Optional: parts saved before it existed lack it.
  'openai-compaction': { id: string; encrypted_content: string; model?: string };
};
type ChatUIMessage = UIMessage<unknown, ChatDataParts, ChatTools>;

/**
 * Format a dollar cost for the chat UI. Uses 2-significant-digit precision
 * below $1 so we don't display "$0.00" when a real cost is in the $0.001-$0.009
 * range. Returns "—" when cost is unknown (older messages with no estimatedCost).
 */
function formatCost(cost: number | undefined): string {
  if (cost === undefined || cost === null) return '—';
  if (cost === 0) return '$0';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
}

const STORAGE_KEY_MESSAGES = 'aiChatMessages';

const STORAGE_KEY_USAGE_BY_MESSAGE = 'aiChatUsageByMessage';

function loadFromStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveToStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* storage full — non-critical */ }
}

export const FloatingAIChat: React.FC<FloatingAIChatProps> = ({ onClose, onHeightChange, survey, getSurvey, onApplyTransaction, getValidationIssues, surveyName, loadOtherSurvey, getSiblingBranchInfo, branchName, editSafety = 'no-branches', documentKey = '', openRequest }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const selectedPropertyRef = useRef<AIChatOpenRequest['resolveContext'] | null>(null);

  useEffect(() => {
    // Drop context from the previous document without discarding the draft.
    selectedPropertyRef.current = null;
  }, [documentKey]);
  // Deliberately not persisted: write access should never be silently still on
  // from a previous session — the user re-opts in (and re-reads the warning).
  const [editMode, setEditMode] = useState(false);
  const [editWarningOpen, setEditWarningOpen] = useState(false);
  // Ref so the memoized transport sees the current value — tool-result
  // auto-continuations don't go through sendMessage.
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  /**
   * PII Mode: this conversation may hold real applicant data. Not persisted, for the same
   * reason as editMode — a mode that changes what the agent may touch should never be
   * silently still on from a previous session.
   *
   * While it is on: nothing is written to localStorage, no tool can write anywhere, and the
   * model is restricted to OpenAI (the only provider we can currently audit and expire).
   */
  const [piiMode, setPiiMode] = useState(false);
  const [piiWarningOpen, setPiiWarningOpen] = useState(false);
  const piiModeRef = useRef(piiMode);
  piiModeRef.current = piiMode;
  /**
   * How many messages predate PII Mode being switched on. Everything from here on exists
   * only in memory, so on switching off we drop back to this point before persistence
   * resumes — otherwise the next message change would write the whole array, applicant data
   * included, to localStorage.
   */
  const piiBoundaryRef = useRef(0);
  /**
   * Identity of the branch the user actually confirmed for. Assigned during render,
   * so the write gate sees a branch change on the very first render after it — the
   * reset effect below runs too late to stop a tool callback that is already
   * streaming.
   */
  const branchKey = `${documentKey}|${editSafety}`;
  const branchKeyRef = useRef(branchKey);
  branchKeyRef.current = branchKey;
  // Refs, not direct prop reads: tool callbacks can outlive the render that
  // captured them, and comparing against a stale survey/branch name would route
  // a "current document" read to the remote snapshot path (or vice versa).
  const surveyNameRef = useRef(surveyName);
  surveyNameRef.current = surveyName;
  const currentBranchNameRef = useRef(branchName);
  currentBranchNameRef.current = branchName;
  const loadOtherSurveyRef = useRef(loadOtherSurvey);
  loadOtherSurveyRef.current = loadOtherSurvey;

  // Same sibling rule as Config's getSiblingBranchInfo — named here so the
  // toggle can say what it does before (or without) the branch fetch resolving.
  const siblingName = surveyName === SurveyName.ENTIRE_PROGRAM ? SurveyName.ADMIN_PANEL : SurveyName.ENTIRE_PROGRAM;

  /**
   * Cross-survey master switch: whether the AI may read the sibling document at
   * all. Persisted (unlike edit mode) — it gates reads only, so there is no
   * safety reason to make the user re-opt-in every session.
   */
  const [crossSurveyEnabled, setCrossSurveyEnabled] = useState(() => localStorage.getItem('aiChatCrossSurvey') !== 'off');
  const crossSurveyEnabledRef = useRef(crossSurveyEnabled);
  crossSurveyEnabledRef.current = crossSurveyEnabled;
  const toggleCrossSurvey = useCallback((checked: boolean) => {
    setCrossSurveyEnabled(checked);
    try { localStorage.setItem('aiChatCrossSurvey', checked ? 'on' : 'off'); } catch { /* storage full — non-critical */ }
  }, []);

  /**
   * Sibling-branch selector: the USER pins which branch of the sibling document
   * the AI reads, defaulting to the sibling's dev (default) branch. The model
   * has no say.
   */
  const [siblingInfo, setSiblingInfo] = useState<{ survey: string, branches: { id: number, name: string, is_dev_branch: boolean }[] } | null>(null);
  const [siblingBranch, setSiblingBranch] = useState<string | undefined>(undefined);
  const siblingBranchRef = useRef(siblingBranch);
  siblingBranchRef.current = siblingBranch;
  const siblingSurveyRef = useRef<string | undefined>(undefined);
  siblingSurveyRef.current = siblingInfo?.survey;
  useEffect(() => {
    // Refetch on document switch ("Save as", branch change) and reset to the
    // default branch — a selection made for one working context must not
    // silently follow to another.
    setSiblingBranch(undefined);
    setSiblingInfo(null);
    if (!getSiblingBranchInfo || !crossSurveyEnabled) return;
    let cancelled = false;
    getSiblingBranchInfo().then(info => {
      if (cancelled) return;
      setSiblingInfo(info);
      const dev = info?.branches.find(b => b.is_dev_branch) ?? info?.branches[0];
      setSiblingBranch(dev?.name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [documentKey, crossSurveyEnabled]);
  const authorizedBranchRef = useRef<string | null>(null);
  /**
   * Set when a transaction committed but did not reproduce its validated result
   * (an engine bug — the document is in an unverified state). Hard-disables all
   * further AI writes until the page is reloaded; deliberately NOT resettable
   * from the UI.
   */
  const divergedRef = useRef(false);
  const [diverged, setDiverged] = useState(false);
  const safetyCopy = EDIT_SAFETY_COPY[editSafety];
  // Cleanup on load: closing the panel (or the tab) mid-stream persists
  // incomplete tool parts; nothing can still be in flight at mount time.
  const [initialMessages] = useState<ChatUIMessage[] | undefined>(() => {
    const saved = loadFromStorage<ChatUIMessage[]>(STORAGE_KEY_MESSAGES);
    return saved ? cleanupInterruptedToolParts(saved) : undefined;
  });
  const [usageByMessageId, setUsageByMessageId] = useState<Record<string, UsageStats>>(() => loadFromStorage(STORAGE_KEY_USAGE_BY_MESSAGE) ?? {});
  const pendingUsageRef = useRef<UsageStats | null>(null);
  const [selectedModel, setSelectedModel] = useState<AIModel>(() => {
    const saved = localStorage.getItem('aiChatModel');
    if (saved && Object.hasOwn(LEGACY_MODEL_ALIASES, saved)) return LEGACY_MODEL_ALIASES[saved];
    return MODEL_OPTIONS.some(o => o.value === saved) ? (saved as AIModel) : 'claude-sonnet-5-5';
  });
  // Ref so the memoized transport always sends the current model — tool-result
  // auto-continuations don't go through sendMessage and would otherwise omit it.
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;
  // Same ref pattern: the transport checks which model produced a compaction.
  const usageByMessageIdRef = useRef(usageByMessageId);
  usageByMessageIdRef.current = usageByMessageId;
  const [height, setHeight] = useState(() => {
    const saved = localStorage.getItem('aiChatHeight');
    return saved ? parseInt(saved) : 400;
  });

  // Height of the Config editor's top toolbar — the panel grows upward from the bottom
  // so we cap its max height to avoid covering the header.
  const DISTRO_HEADER_HEIGHT = 56;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Sticky scroll: follow new content only while the user is at (or near) the
  // bottom. Scrolling up detaches so they can read history during streaming;
  // scrolling back down re-attaches.
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  // A tool output landing after stop() would auto-resend the request the user
  // just cancelled; sendAutomaticallyWhen checks this to stay cancelled.
  const stopRequestedRef = useRef(false);
  const resizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const [, authParam] = useToken();

  // Extract raw token value (remove "auth=" prefix)
  const rawToken = (authParam || '').replace('auth=', '');

  const apiUrl = `${streamingApiPath()}program/admin/distro_ai_chat`;

  // Condensed messages from backend, used to replace the old prefix on subsequent sends.

  // Create transport for the useChat hook
  const transport = useMemo(
    () => new DefaultChatTransport({
      api: apiUrl,
      // Keep Authorization during the rollout so this frontend remains compatible
      // before OAC is enabled. OAC overwrites it with SigV4 at the origin; the
      // dedicated header carries the AidKit token through to the application.
      headers: {
        'Authorization': `Bearer ${rawToken}`,
        [AIDKIT_STREAMING_AUTH_HEADER]: `Bearer ${rawToken}`,
      },
      fetch: fetchWithAwsPayloadHash,
      prepareSendMessagesRequest: ({ messages, body, headers, credentials, api }) => {
        const model = ((body as any)?.model as string | undefined) ?? selectedModelRef.current;
        // Slice/compaction/tool-strip rules live in aiChatTranscript.ts (unit tested).
        const { messages: messagesToSend, openaiCompaction } =
          prepareOutgoingMessages(messages, model, usageByMessageIdRef.current);
        // One line per request so "the model couldn't find editConfig" is
        // diagnosable from the console: was the flag on THIS request or not?
        console.log('[distroAI] request', { model, allowConfigEdits: editModeRef.current && !piiModeRef.current, piiMode: piiModeRef.current, messageCount: messagesToSend.length });
        return {
          // allowConfigEdits gates the editConfig tool server-side, so unticking
          // the box mid-conversation immediately takes the tool away again.
          body: {
            ...body,
            model,
            messages: messagesToSend,
            // The two are mutually exclusive and the server rejects the combination, so
            // don't even ask: the UI disables the edit checkbox, and this makes a stale
            // editMode from just before the toggle harmless.
            allowConfigEdits: editModeRef.current && !piiModeRef.current,
            piiMode: piiModeRef.current,
            ...(openaiCompaction && { openaiCompaction }),
          },
          headers,
          credentials,
          api,
        };
      },
    }),
    [apiUrl, rawToken]
  );

  // Use the Vercel AI SDK useChat hook
  const {
    messages,
    sendMessage,
    status,
    setMessages,
    error,
    addToolOutput,
    stop,
  } = useChat<ChatUIMessage>({
    messages: initialMessages,
    transport,
    dataPartSchemas: {
      'usage': jsonSchema<UsageStats>({
        type: 'object',
        properties: {
          inputTokens: { type: 'number' },
          outputTokens: { type: 'number' },
          totalTokens: { type: 'number' },
          cacheReadTokens: { type: 'number' },
          cacheWriteTokens: { type: 'number' },
          estimatedCost: { type: 'number' },
          model: { type: 'string' },
        },
        required: ['inputTokens', 'outputTokens', 'totalTokens'],
      }),
      'openai-compaction': jsonSchema<{ id: string; encrypted_content: string; model?: string }>({
        type: 'object',
        properties: {
          id: { type: 'string' },
          encrypted_content: { type: 'string' },
          model: { type: 'string' },
        },
        required: ['id', 'encrypted_content'],
      }),
    },
    onData: (dataPart) => {
      if (dataPart.type === 'data-usage') {
        const usage = dataPart.data as UsageStats;
        pendingUsageRef.current = usage;
      }
    },
    onFinish: ({ message }) => {
      const usage = pendingUsageRef.current;
      if (!usage) return;
      pendingUsageRef.current = null;
      setUsageByMessageId(prev => ({ ...prev, [message.id]: usage }));
    },
    onToolCall: async ({ toolCall }: { toolCall: { toolCallId: string; toolName: string; input: unknown } }) => {
      console.log('Frontend tool call:', toolCall.toolName, toolCall.input);
      const toolInput = toolCall.input as Record<string, any>;

      // Latest document, so a read after an edit in the same turn isn't stale.
      const currentSurvey = getSurvey?.() ?? survey ?? null;

      const FRONTEND_TOOLS = ['getConfigSummary', 'searchConfig', 'getObject', 'editConfig', 'copyConfigObject', 'checkConfigValidity'] as const;
      type FrontendTool = typeof FRONTEND_TOOLS[number];
      const isFrontendTool = (n: string): n is FrontendTool =>
        (FRONTEND_TOOLS as readonly string[]).includes(n);

      if (!isFrontendTool(toolCall.toolName)) return;
      const frontendToolName: FrontendTool = toolCall.toolName;

      // Unknown parameters are a hard error, never silently ignored: a model that
      // invents a param (or misnames survey/branch) would otherwise get the open
      // document back while believing it read something else.
      const KNOWN_PARAMS: Record<FrontendTool, readonly string[]> = {
        getConfigSummary: ['survey', 'branch'],
        searchConfig: ['pattern', 'field', 'offset', 'survey', 'branch'],
        getObject: ['path', 'showAllLangs', 'survey'],
        editConfig: ['edits'],
        copyConfigObject: ['fromPath', 'toPath', 'fromSurvey', 'op', 'index'],
        checkConfigValidity: ['survey'],
      };
      const unknownParams = Object.keys(toolInput ?? {}).filter(k => !KNOWN_PARAMS[frontendToolName].includes(k));

      // Cross-survey routing: a survey param naming anything other than the open
      // document reads a remote snapshot instead of the live editor doc. WHICH
      // branch is the user's choice (the panel's sibling-branch selector), never
      // the model's — undefined = the target's default (dev) branch.
      const pinnedBranchFor = (name: string): string | undefined =>
        (name === siblingSurveyRef.current ? siblingBranchRef.current : undefined);
      const remoteTarget = (() => {
        const wantedSurvey = typeof toolInput.survey === 'string' && toolInput.survey.length > 0 ? toolInput.survey : undefined;
        if (wantedSurvey === undefined || wantedSurvey === surveyNameRef.current) return null;
        return { name: wantedSurvey };
      })();

      let result: Record<string, unknown> | undefined;
      // Which document the read tools see, plus snapshot metadata echoed in the
      // result so the model knows it is not looking at the live open document.
      let doc = currentSurvey;
      let remoteMeta: Record<string, unknown> | undefined;
      // The write tools resolve their own source (copyConfigObject uses
      // fromSurvey/fromBranch) and must never have their write redirected.
      const isWriteTool = toolCall.toolName === 'editConfig' || toolCall.toolName === 'copyConfigObject';
      try {
        if (unknownParams.length > 0) {
          result = {
            error: `Unknown parameter${unknownParams.length > 1 ? 's' : ''} for ${frontendToolName}: ${unknownParams.join(', ')}. `
              + `Valid parameters: ${KNOWN_PARAMS[frontendToolName].join(', ') || '(none)'}. The call was NOT executed.`,
          };
        } else if (remoteTarget && !isWriteTool) {
          if (!crossSurveyEnabledRef.current) {
            result = { error: 'Cross-survey reads are turned off. Ask the user to enable the "Cross-survey" toggle in the chat panel if you need the sibling document.' };
          } else if (!loadOtherSurveyRef.current || !remoteTarget.name) {
            result = { error: 'Reading another survey document is not available here.' };
          } else {
            const remote = await loadOtherSurveyRef.current(remoteTarget.name, pinnedBranchFor(remoteTarget.name));
            doc = remote.survey;
            remoteMeta = {
              survey: remote.surveyName,
              ...(remote.branchName !== undefined && { branch: remote.branchName }),
              fetchedAt: new Date(remote.fetchedAt).toISOString(),
              note: 'Snapshot of a different survey document (cached up to 30s), not the live open document.',
            };
          }
        }

        if (result !== undefined) {
          // Routing already failed; skip the tool body.
        } else switch (toolCall.toolName) {
          case 'getConfigSummary':
            result = getConfigSummary(doc);
            console.log('Frontend tool result:', toolCall.toolName, JSON.stringify(result).length, 'chars');
            break;
          case 'searchConfig':
            result = searchConfig(doc, toolInput.pattern, toolInput.field, toolInput.offset);
            console.log('Frontend tool result:', toolCall.toolName, result);
            break;
          case 'getObject':
            result = getObject(doc, toolInput.path, toolInput.showAllLangs);
            console.log('Frontend tool result:', toolCall.toolName, JSON.stringify(result).length, 'chars');
            break;
          case 'editConfig': {
            if (remoteTarget) {
              result = { error: 'editConfig always writes the document open in the editor. Cross-survey editing is not available yet — do not pass survey to editConfig.' };
              break;
            }
            // Client-side gates, independent of the server's tool inclusion: a
            // tool call arriving from a stale in-flight turn must not write after
            // the box is unticked. Enforced inside runEditConfig so no call site
            // can reach the write path ungated.
            result = runEditConfig(toolInput, onApplyTransaction, {
              editModeEnabled: editModeRef.current,
              stopRequested: stopRequestedRef.current,
              branchChanged: authorizedBranchRef.current !== branchKeyRef.current,
              diverged: divergedRef.current,
              piiMode: piiModeRef.current,
            });
            if ((result as { diverged?: boolean }).diverged) {
              // The document no longer matches anything that was validated. Stop
              // all further AI writes until the user reloads.
              divergedRef.current = true;
              setDiverged(true);
            }
            console.log('Frontend tool result:', toolCall.toolName, result);
            break;
          }
          case 'copyConfigObject': {
            if (remoteTarget) {
              result = { error: 'copyConfigObject selects its source with fromSurvey, and always writes the open document — do not pass survey.' };
              break;
            }
            const fromSurvey = typeof toolInput.fromSurvey === 'string' && toolInput.fromSurvey.length > 0 ? toolInput.fromSurvey : undefined;
            const sourceIsRemote = fromSurvey !== undefined && fromSurvey !== surveyNameRef.current;

            let sourceDoc: Root | null = currentSurvey;
            let sourceMeta: Record<string, unknown> | undefined;
            if (sourceIsRemote) {
              const sourceName = fromSurvey;
              if (!crossSurveyEnabledRef.current) {
                result = { error: 'Cross-survey reads are turned off. Ask the user to enable the "Cross-survey" toggle in the chat panel to copy from the sibling document.' };
                break;
              }
              if (!loadOtherSurveyRef.current) {
                result = { error: 'Reading another survey document is not available here.' };
                break;
              }
              // Source branch is the user's sibling-branch selection, never the model's.
              const remote = await loadOtherSurveyRef.current(sourceName, pinnedBranchFor(sourceName));
              sourceDoc = remote.survey;
              sourceMeta = {
                survey: remote.surveyName,
                ...(remote.branchName !== undefined && { branch: remote.branchName }),
                fetchedAt: new Date(remote.fetchedAt).toISOString(),
              };
            }

            result = runCopyConfig(
              toolInput,
              (p) => sourceDoc ? resolveRawPath(sourceDoc, p) : { error: 'No survey loaded' },
              onApplyTransaction,
              {
                editModeEnabled: editModeRef.current,
                stopRequested: stopRequestedRef.current,
                branchChanged: authorizedBranchRef.current !== branchKeyRef.current,
                diverged: divergedRef.current,
                piiMode: piiModeRef.current,
              },
            );
            if (sourceMeta) result = { ...result, copiedFrom: sourceMeta };
            if ((result as { diverged?: boolean }).diverged) {
              divergedRef.current = true;
              setDiverged(true);
            }
            console.log('Frontend tool result:', toolCall.toolName, result);
            break;
          }
          case 'checkConfigValidity': {
            // Also treats an absent getValidationIssues prop as not-loaded: a caller
            // that can't supply validation must not get "valid: true".
            const failures = getValidationIssues?.(remoteMeta ? doc ?? undefined : undefined) ?? null;
            if (failures === null) {
              result = { error: 'No survey loaded' };
              break;
            }
            const { issues, totalCount } = formatIssues(failures);
            result = totalCount === 0
              ? { valid: true, totalCount: 0 }
              : { valid: false, totalCount, issues };
            console.log('Frontend tool result:', toolCall.toolName, totalCount, 'failures');
            break;
          }
        }
      } catch (e) {
        result = { error: `Tool "${toolCall.toolName}" failed: ${e instanceof Error ? e.message : String(e)}` };
      }

      if (result !== undefined) {
        // Label every read (success or error) with the document it actually came
        // from — live reads too, so a call that silently resolved to the open
        // document can never be mistaken for a sibling-survey snapshot.
        const isReadTool = !isWriteTool;
        const docMeta = remoteMeta ?? (isReadTool ? {
          survey: surveyNameRef.current ?? 'unknown',
          ...(currentBranchNameRef.current !== undefined && { branch: currentBranchNameRef.current }),
          live: true,
        } : undefined);
        if (docMeta) result = { document: docMeta, ...result };
        // Oversized results are trimmed, never turned into an error for a write —
        // see boundToolResult.
        result = boundToolResult(result);

        // A late output would overwrite the 'Stopped by user' error state
        // requestStop just wrote, resurrecting the cancelled call as successful.
        if (stopRequestedRef.current) return;
        addToolOutput({
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          output: result,
        });
      }
    },
    sendAutomaticallyWhen: (args) => !stopRequestedRef.current && lastAssistantMessageIsCompleteWithToolCalls(args),
    onError: (error: Error) => {
      console.error('Chat error:', error);
    },
  });


  const isLoading = status !== 'ready' && status !== 'error';

  const requestStop = useCallback(() => {
    stopRequestedRef.current = true;
    stop();
    // stop() leaves mid-flight tool parts pending forever (and they persist to
    // localStorage); clean them up so the UI doesn't show a stuck spinner.
    setMessages(cleanupInterruptedToolParts);
  }, [stop, setMessages]);

  // Closing mid-stream should cancel the request like Stop/Esc does — otherwise
  // the backend keeps generating with nobody listening. Message cleanup is
  // handled by the on-load pass, since the persistence effect may not run
  // again before unmount.
  const handleClose = useCallback(() => {
    if (isLoading) requestStop();
    onClose();
  }, [isLoading, requestStop, onClose]);

  useEffect(() => {
    if (!isLoading) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      // Escape aimed at another editing context shouldn't kill the stream:
      // anywhere on the page, an open dropdown/menu/dialog — including portaled
      // listboxes rendered at document.body and the panel's own model picker
      // (an expanded combobox input) — and outside the panel, any text-editing
      // element. The chat textarea itself stays a valid Esc-to-stop surface.
      const target = e.target as HTMLElement | null;
      if (target?.closest('[role="dialog"], [role="listbox"], [role="menu"], [role="option"], [aria-expanded="true"]')) return;
      if (target && !panelRef.current?.contains(target)
        && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      requestStop();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLoading, requestStop]);

  // Approximate context size of the next request: input + output tokens of the
  // most recent assistant message that reported usage.
  const conversationTokens = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      const usage = usageByMessageId[msg.id];
      if (usage) return (usage.inputTokens ?? 0) + (usage.outputTokens ?? 0);
    }
    return 0;
  }, [messages, usageByMessageId]);
  // Once a provider has compacted this conversation, its reported usage only
  // reflects the compacted context — but history slicing is provider-specific,
  // so switching providers would resend the full uncompacted history. Block
  // cross-provider switches from then on.
  const compactedProvider = useMemo((): 'anthropic' | 'openai' | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const parts = messages[i].parts ?? [];
      if (parts.some((p: any) => p.providerMetadata?.anthropic?.type === 'compaction')) return 'anthropic';
      if (parts.some((p: any) => p.type === 'data-openai-compaction')) return 'openai';
    }
    return null;
  }, [messages]);
  const modelSelectOptions = useMemo(() => MODEL_OPTIONS.map((option) => {
    // Never block the currently selected model; provider-side compaction
    // handles growth there. Only prevent switching TO a model whose context
    // window the conversation would overflow.
    const isCandidate = option.value !== selectedModel;
    const tooSmall = isCandidate
      && conversationTokens > 0.9 * MODEL_CONTEXT_WINDOWS[option.value];
    const optionProvider = option.value.startsWith('claude-') ? 'anthropic' : 'openai';
    const crossProvider = isCandidate
      && compactedProvider !== null && optionProvider !== compactedProvider;
    // PII mode is OpenAI-only: the server rejects anything else, so don't offer it.
    const wrongProviderForPii = piiMode && optionProvider !== 'openai';
    const reason = wrongProviderForPii
      ? 'PII mode is OpenAI-only'
      : tooSmall
        ? 'conversation too large'
        : crossProvider ? 'can’t switch providers after compaction' : null;
    return modelSelectOption(option, reason);
  }), [selectedModel, conversationTokens, compactedProvider, piiMode]);
  const cumulativeUsage = useMemo(() => {
    const initial: { inputTokens: number; outputTokens: number; totalTokens: number; estimatedCost: number } = {
      inputTokens: 0, outputTokens: 0, totalTokens: 0, estimatedCost: 0,
    };
    return Object.values(usageByMessageId).reduce(
      (acc, usage) => ({
        inputTokens: acc.inputTokens + usage.inputTokens,
        outputTokens: acc.outputTokens + usage.outputTokens,
        totalTokens: acc.totalTokens + usage.totalTokens,
        estimatedCost: acc.estimatedCost + (usage.estimatedCost ?? 0),
      }),
      initial,
    );
  }, [usageByMessageId]);


  // Persist full message history to localStorage.
  // Not in PII mode: the transcript may hold applicant data and must not reach disk. The
  // pre-PII prefix stays saved from before the toggle, which is why switching the mode off
  // truncates back to the boundary first — this effect writes the WHOLE array, so it would
  // otherwise persist the applicant turns on the next message change.
  useEffect(() => {
    if (piiMode) return;
    if (messages.length > 0) {
      saveToStorage(STORAGE_KEY_MESSAGES, messages);
    }

  }, [messages, piiMode]);

  useEffect(() => {
    if (piiMode) return;
    saveToStorage(STORAGE_KEY_USAGE_BY_MESSAGE, usageByMessageId);
  }, [usageByMessageId, piiMode]);

  // Fallback path if usage data arrives after onFinish: bind it to the latest assistant message.
  useEffect(() => {
    if (!pendingUsageRef.current || status !== 'ready') return;
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant || usageByMessageId[lastAssistant.id]) return;
    const usage = pendingUsageRef.current;
    pendingUsageRef.current = null;
    setUsageByMessageId(prev => ({ ...prev, [lastAssistant.id]: usage }));
  }, [messages, status, usageByMessageId]);

  // Our own scrolling only ever moves toward the bottom, so any decrease in
  // scrollTop can only come from the user (wheel, touch, or scrollbar drag) —
  // no need to distinguish gesture types. Checked before the decrease so that
  // e.g. growing the panel while stuck at the bottom (which can clamp
  // scrollTop down) re-attaches instead of reading as a user scroll-up.
  // 40px of slack so tiny offsets (subpixel rounding, momentum) still count as
  // bottom. `onScroll` doesn't bubble from nested scrollable regions (like a
  // tool's JSON preview), so scrolling inside one never affects this.
  const onMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const { scrollTop } = el;
    if (el.scrollHeight - scrollTop - el.clientHeight < 40) {
      stickToBottomRef.current = true;
    } else if (scrollTop < lastScrollTopRef.current) {
      stickToBottomRef.current = false;
    }
    lastScrollTopRef.current = scrollTop;
  };

  // Auto-scroll: instant during streaming (to avoid queued smooth animations fighting),
  // smooth when a new message arrives while idle. Skipped while the user has
  // scrolled up to read.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: isLoading ? 'instant' : 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    localStorage.setItem('aiChatHeight', height.toString());
    onHeightChange?.(isMinimized ? 0 : height);
  }, [height, isMinimized]);

  // Clamp height to viewport on mount and whenever the window is resized,
  // so the resize handle can never end up behind the top header.
  useEffect(() => {
    const clampHeight = () => {
      const maxHeight = window.innerHeight - DISTRO_HEADER_HEIGHT;
      setHeight(h => Math.min(h, maxHeight));
    };
    clampHeight();
    window.addEventListener('resize', clampHeight);
    return () => window.removeEventListener('resize', clampHeight);
  }, []);
  // Save model selection to localStorage
  useEffect(() => {
    localStorage.setItem('aiChatModel', selectedModel);
  }, [selectedModel]);

  // Clears the checkbox after a branch change. The gate does not depend on this
  // running — it compares the authorized branch itself — but the UI must not keep
  // showing edit mode as on for a branch it was never authorized for.
  useEffect(() => {
    setEditMode(false);
    authorizedBranchRef.current = null;
  }, [branchKey]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = height;
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizingRef.current) return;
    const deltaY = startYRef.current - e.clientY;
    const maxHeight = window.innerHeight - DISTRO_HEADER_HEIGHT;
    const newHeight = Math.max(200, Math.min(maxHeight, startHeightRef.current + deltaY));
    setHeight(newHeight);
  }, []);

  const handleMouseUp = useCallback(() => {
    resizingRef.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleClearChat = () => {
    if (confirm('Clear all chat messages?')) {
      // Cancel any in-flight response first — otherwise the fetch and the
      // server-side generation keep running, and late stream chunks push
      // content back into the transcript we just emptied.
      if (isLoading) requestStop();
      setMessages([]);
      // Everything present after this is post-PII, so the old boundary would keep applicant
      // turns on the way out of the mode.
      piiBoundaryRef.current = 0;
      pendingUsageRef.current = null;
      setUsageByMessageId({});
      localStorage.removeItem(STORAGE_KEY_MESSAGES);
      localStorage.removeItem(STORAGE_KEY_USAGE_BY_MESSAGE);
    }
  };

  /**
   * Enabling PII Mode. Everything from this point exists only in memory, so the boundary is
   * recorded here and edit mode is forced off — the two are mutually exclusive and the
   * server refuses the combination.
   *
   * An in-flight turn is stopped first: it was authorized under the old mode, and its tool
   * results would arrive into a conversation that has since promised not to write anywhere.
   */
  const enablePiiMode = () => {
    if (isLoading) requestStop();
    // requestStop prunes an interrupted turn, which can drop a message entirely, and
    // `messages` here is the pre-stop render value. Counting the cleaned array keeps the
    // boundary from pointing past the end.
    piiBoundaryRef.current = cleanupInterruptedToolParts(messages).length;
    setEditMode(false);
    // The dropdown disables the Anthropic options, but never the currently selected one --
    // so without this, turning the mode on while on Claude would send a request the server
    // refuses. Switch rather than block, so the mode is always reachable.
    if (!selectedModel.startsWith('gpt-')) setSelectedModel(PII_MODE_DEFAULT_MODEL);
    setPiiMode(true);
    setPiiWarningOpen(false);
  };

  /**
   * Leaving PII Mode drops every message added while it was on, before persistence resumes.
   * Without this the next message change would write the whole array — applicant turns
   * included — to localStorage, which is the one thing the mode promised not to do.
   */
  const disablePiiMode = () => {
    if (!confirm('Leaving PII mode discards the applicant data in this conversation. Continue?')) return;
    if (isLoading) requestStop();
    setMessages(current => messagesAfterLeavingPiiMode(current, piiBoundaryRef.current));
    setPiiMode(false);
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const selection = selectedPropertyRef.current;
    const text = selection ? buildAskAIMessage(input, selection) : input;
    stopRequestedRef.current = false;
    // Sending always jumps to the new message, even if scrolled up.
    stickToBottomRef.current = true;
    setInput('');
    selectedPropertyRef.current = null;
    sendMessage({ text }, { body: { model: selectedModel } });
  };

  // Auto-grow the input with its content. The native resize grip is disabled —
  // dragging it grew the textarea downward past the fixed-height panel and off
  // screen. Large pastes need reviewing, so allow up to 500px — clamped to the
  // space the panel actually has (everything but the header and a strip of
  // conversation), so the default 400px-tall panel isn't overflowed. Dragging
  // the panel taller unlocks the full 500px; past the cap the draft scrolls.
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!openRequest) return;
    setIsMinimized(false);
    if (input.trim() && !confirm('Clear your unsent question and replace it with a question about this property?')) return;
    selectedPropertyRef.current = openRequest.resolveContext;
    setInput(openRequest.draft);
    const frame = window.requestAnimationFrame(() => {
      const input = inputTextAreaRef.current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openRequest]);

  useEffect(() => {
    const el = inputTextAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxInputHeight = Math.min(500, Math.max(200, height - 240));
    el.style.height = `${Math.min(el.scrollHeight, maxInputHeight)}px`;
  }, [input, height]);

  return (
    <div
      ref={panelRef}
      className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 shadow-2xl z-50 flex flex-col"
      style={{ height: isMinimized ? 'auto' : `${height}px` }}
    >
      {/* Override survey-specific markdown hr rules inside AI chat */}
      <style>{`
        .ai-chat-markdown .markdown p,
        .ai-chat-markdown .markdown ul,
        .ai-chat-markdown .markdown ol,
        .ai-chat-markdown .markdown pre,
        .ai-chat-markdown .markdown blockquote,
        .ai-chat-markdown .markdown h1,
        .ai-chat-markdown .markdown h2,
        .ai-chat-markdown .markdown h3,
        .ai-chat-markdown .markdown h4,
        .ai-chat-markdown .markdown h5,
        .ai-chat-markdown .markdown h6 { margin: 0.15em 0 !important; }
        .ai-chat-markdown .markdown ul,
        .ai-chat-markdown .markdown ol { padding-left: 1.25em !important; margin-bottom: 0.15em !important; }
        .ai-chat-markdown .markdown li { margin: 0 !important; }
        .ai-chat-markdown .markdown h1,
        .ai-chat-markdown .markdown h2 { font-size: 0.95em !important; font-weight: 600; }
        .ai-chat-markdown .markdown h3,
        .ai-chat-markdown .markdown h4,
        .ai-chat-markdown .markdown h5,
        .ai-chat-markdown .markdown h6 { font-size: 0.875em !important; font-weight: 600; }
        .ai-chat-markdown .markdown hr,
        .ai-chat-markdown .markdown hr ~ * { display: revert; }
        .ai-chat-markdown .markdown hr + p {
          color: inherit; cursor: auto; border: none;
          border-radius: 0; padding: 0; font-size: inherit;
        }
        .ai-chat-markdown .markdown hr + p::after { content: none; }
      `}</style>
      {/* Resize handle */}
      {!isMinimized && (
        <div
          className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-blue-200 transition-colors"
          onMouseDown={handleMouseDown}
        >
          <div className="w-12 h-1 bg-gray-400 rounded mx-auto mt-0.5" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Icon name="SparklesIcon" className="text-blue-500" size="sm" />
          <span className="font-semibold text-gray-700">AI Assistant</span>
          {messages.length > 0 && (
            <span className="text-xs text-gray-500">({messages.filter((m: ChatUIMessage) => m.role === 'user').length} messages)</span>
          )}
          {cumulativeUsage.totalTokens > 0 && (
            <span className="text-xs text-gray-500"
              title={`Cumulative: ${cumulativeUsage.inputTokens.toLocaleString()} input, ${cumulativeUsage.outputTokens.toLocaleString()} output, ${cumulativeUsage.totalTokens.toLocaleString()} total tokens`}
            >
              {formatCost(cumulativeUsage.estimatedCost)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onApplyTransaction && (
            <>
              <div className="text-sm" title={piiMode ? 'Disabled while PII mode is on — the agent must not be able to write applicant data into the config.' : undefined}>
                <Checkbox
                  label="Allow edits"
                  checked={editMode}
                  disabled={piiMode}
                  // Ticking only opens the warning; the box turns on when it's
                  // acknowledged. Unticking takes effect at once, even mid-stream.
                  onChange={(checked) => {
                    if (checked) setEditWarningOpen(true);
                    else setEditMode(false);
                  }}
                />
              </div>
              <div className="w-px h-4 bg-gray-300" />
              <div className="text-sm" title={editMode ? 'Disabled while edits are allowed — turn edits off first.' : 'Look at real applicant data. Nothing is written to disk, and no tool can write anywhere.'}>
                <Checkbox
                  label="PII mode"
                  checked={piiMode}
                  disabled={editMode}
                  // Same shape as Allow edits: turning it on goes through the warning,
                  // turning it off is immediate but discards the applicant turns.
                  onChange={(checked) => {
                    if (checked) setPiiWarningOpen(true);
                    else disablePiiMode();
                  }}
                />
              </div>
              <div className="w-px h-4 bg-gray-300" />
            </>
          )}
          {/* Cross-survey master switch: whether the AI may read the sibling document. */}
          {getSiblingBranchInfo && (
            <>
              <div className="text-sm" title={`Let the AI read the ${siblingName} survey document`}>
                <Checkbox
                  label={`Read ${siblingName}`}
                  checked={crossSurveyEnabled}
                  onChange={(checked) => toggleCrossSurvey(!!checked)}
                />
              </div>
              <div className="w-px h-4 bg-gray-300" />
            </>
          )}
          {/* Sibling-branch selector: the user pins which branch of the sibling
              survey document the AI reads — the model has no branch parameter.
              Absent entirely when the sibling has no branches (published doc). */}
          {crossSurveyEnabled && siblingInfo && siblingInfo.branches.length > 0 && (() => {
            // Same display convention as BranchDropdown: the dev branch reads
            // "default" (not its stored name) and sorts first.
            const sorted = [...siblingInfo.branches].sort((a, b) => (a.is_dev_branch ? -1 : b.is_dev_branch ? 1 : 1));
            const displayName = (name: string) =>
              siblingInfo.branches.find(b => b.name === name)?.is_dev_branch ? 'default' : name;
            const options = sorted.map(b => ({
              value: b.name,
              label: b.is_dev_branch ? 'default' : b.name,
            }));
            return (
              <>
                <div className="w-32 text-sm" title={`Which branch of ${siblingInfo.survey} the AI reads`}>
                  <Select
                    hideLabel
                    label={`${siblingInfo.survey} branch`}
                    description={`Which branch of ${siblingInfo.survey} the AI reads`}
                    value={options.find(o => o.value === siblingBranch) ?? options[0]}
                    onChange={(option) => setSiblingBranch(option.value)}
                    options={options}
                    customButtonTextRenderer={(value) => displayName(value)}
                    portal
                  />
                </div>
                <div className="w-px h-4 bg-gray-300" />
              </>
            );
          })()}
          {/* Model Selector */}
          <div className="w-44 text-sm">
            <Select
              hideLabel
              label="AI model"
              description="Select AI model"
              value={modelSelectOptions.find((o) => o.value === selectedModel)}
              onChange={(option) => setSelectedModel(option.value)}
              options={modelSelectOptions}
              customButtonTextRenderer={(value) => MODEL_OPTIONS.find((o) => o.value === value)?.label ?? value}
              // The chat panel is z-50; without portal the anchored dropdown renders at z-20 behind it.
              portal
            />
          </div>
          <div className="w-px h-4 bg-gray-300" />
          <button
            onClick={handleClearChat}
            className="p-1 hover:bg-gray-200 rounded"
            title="Clear chat"
          >
            <Icon name="FireIcon" size="sm" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-gray-200 rounded"
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            <Icon name={isMinimized ? 'ChevronUpIcon' : 'ChevronDownIcon'} size="sm" />
          </button>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-200 rounded"
            title="Close"
          >
            <Icon name="XMarkIcon" size="sm" />
          </button>
        </div>
      </div>

      {/* Chat content */}
      {!isMinimized && (
        <>
          {diverged && (
            <div className="flex items-start gap-2 px-3 py-2 border-b text-xs bg-red-50 border-red-200 text-red-800">
              <Icon name="ExclamationTriangleIcon" size="sm" className="shrink-0 mt-0.5" />
              <span>
                <strong>An AI edit left this config in an unverified state.</strong>{' '}
                AI editing is disabled. Reload the page, check the Changes tab, and consider
                discarding this branch.
              </span>
            </div>
          )}
          {editMode && (
            <div className={`flex items-start gap-2 px-3 py-2 border-b text-xs ${safetyCopy.banner}`}>
              <Icon name="ExclamationTriangleIcon" size="sm" className="shrink-0 mt-0.5" />
              <span>{safetyCopy.message(branchName)}</span>
            </div>
          )}
          {piiMode && (
            <div className="flex items-start gap-2 px-3 py-2 border-b text-xs bg-blue-50 border-blue-200 text-blue-900">
              <Icon name="ExclamationTriangleIcon" size="sm" className="shrink-0 mt-0.5" />
              <span>
                <strong>PII mode.</strong> This conversation is kept in memory only — it is discarded on
                reload, on closing this panel, and when you switch the mode off. Nothing here can be
                written to the config or to a planning doc — check what you copy out for applicant data.
              </span>
            </div>
          )}
          {/* Messages */}
          {/* flex-1/min-h-0 (not fixed calc math): the input area below can grow
              when the user types a long message, and the messages area shrinks
              to make room — the input expands upward instead of off screen. */}
          <div
            ref={messagesContainerRef}
            onScroll={onMessagesScroll}
            className="overflow-y-auto px-3 py-2 space-y-2 flex-1 min-h-0"
          >
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                <Icon name="SparklesIcon" className="mx-auto mb-2 text-blue-400" size="lg" />
                <p className="text-sm">Ask me anything about your survey configuration!</p>
                <p className="text-xs mt-2">Try: "How do I add a conditional field?" or "Explain this notification"</p>
              </div>
            )}
            {messages.map((message: ChatUIMessage) => (
              <React.Fragment key={message.id}>
                {message.role === 'assistant' ? (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] bg-gray-100 rounded-lg px-3 py-1.5">
                      {message.parts.map((part, i) => {
                        if (part.type === 'text' && part.text) {
                          return <AssistantUIMessage key={i} content={part.text} />;
                        }
                        if (isToolUIPart(part)) {
                          return <ToolInvocationPart key={i} part={part} />;
                        }
                        return null;
                      })}
                      {usageByMessageId[message.id] && (
                        <div className="mt-2 border-t border-gray-200 pt-2 text-[11px] text-gray-500"
                          title={`${usageByMessageId[message.id].inputTokens.toLocaleString()} in, ${usageByMessageId[message.id].outputTokens.toLocaleString()} out, ${usageByMessageId[message.id].totalTokens.toLocaleString()} total tokens`}
                        >
                          {formatCost(usageByMessageId[message.id].estimatedCost)}
                        </div>
                      )}
                    </div>
                  </div>
                ) : message.role === 'user' ? (
                  <div className="flex justify-end">
                    <div
                      className="max-w-[80%] rounded-lg px-3 py-1.5 text-white"
                      style={{ backgroundColor: '#013F7A' }}
                    >
                      <div className="text-sm break-words whitespace-pre-wrap">
                        {getMessageText(message)}
                      </div>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
            {error && (
              <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                Error: {error.message}
              </div>
            )}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-3 py-1.5">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <div className="border-t border-gray-200 px-4 py-4 pb-6 bg-white">
            <form onSubmit={onFormSubmit} className="flex gap-2 items-end">
              <div className="flex-1">
                <TextArea
                  ref={inputTextAreaRef}
                  label="Message"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onFormSubmit(e as any);
                    }
                  }}
                  placeholder={isLoading ? 'Press Esc to stop the current request' : 'Ask about your survey...'}
                  rows={2}
                  className="text-sm"
                  textareaClassName="resize-none overflow-y-auto"
                />
              </div>
              {isLoading ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={requestStop}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  isDisabled={!input.trim()}
                >
                  Send
                </Button>
              )}
            </form>
          </div>
        </>
      )}

      <Modal
        open={editWarningOpen}
        onClose={() => setEditWarningOpen(false)}
        title="Let the assistant edit this config?"
        size="md"
        icon={<Icon name="ExclamationTriangleIcon" className="text-amber-500" />}
      >
        <div className="text-sm text-gray-700 space-y-3">
          <p>
            With edits on, the assistant changes your working copy of the config directly — the same
            as if you had typed the change yourself. There's no per-edit confirmation.
          </p>
          <p>
            <strong>Only use this on a non-default branch.</strong> Then you can throw away everything
            it did by deleting the branch. Anywhere else you'd have to undo each change by hand.
          </p>
          {safetyCopy.modalNote && (
            <p className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
              {safetyCopy.modalNote(branchName)}
            </p>
          )}
          <p className="text-xs text-gray-500">
            Edits show up in the Changes tab and are not published until you publish.
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditWarningOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => {
              setEditMode(true);
              authorizedBranchRef.current = branchKeyRef.current;
              setEditWarningOpen(false);
            }}
          >
            {safetyCopy.confirmLabel}
          </Button>
        </div>
      </Modal>

      <Modal
        open={piiWarningOpen}
        onClose={() => setPiiWarningOpen(false)}
        title="Turn on PII mode?"
        size="md"
        icon={<Icon name="ExclamationTriangleIcon" className="text-blue-500" />}
      >
        <div className="text-sm text-gray-700 space-y-3">
          <p>
            PII mode lets you look at real applicant data in this chat. In exchange, the assistant
            loses every tool that writes anywhere — no config edits, no planning docs — and this
            conversation is kept in memory only.
          </p>
          <p>
            <strong>Nothing from here survives a reload</strong>, closing this panel, or switching the
            mode back off. Messages you've already sent stay saved; everything from this point does not.
          </p>
          <p className="rounded bg-blue-50 border border-blue-200 p-2 text-blue-900">
            Allow edits will be turned off, and the model switches to OpenAI — the only provider we can
            currently audit and expire this data with.
          </p>
          <p className="text-xs text-gray-500">
            The one copy we can't remove for you is the one you make: don't paste applicant data from
            here into Slack, a ticket, or a doc.
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPiiWarningOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={enablePiiMode}>Turn on PII mode</Button>
        </div>
      </Modal>
    </div>
  );
};
